import {ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef, MatTableDataSource} from "@angular/material";
import {ActionPointsService} from "../../../../service/club-gigot/action-points.service";
import {Product} from "../../../../persistence/circular-dto/product.conflict";
import {
    ActionPointsDTO,
    AdvanceSaleGroup,
    AdvanceSaleGroupProduct,
} from "../../../../model/club-gigot/action-points/action-points-configuration.model";
import { BehaviorSubject } from "rxjs";
import { finalize } from "rxjs/operators";
import {FormArray, FormControl, FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {
    ActionPointsAdvanceSaleResult
} from "../../../../model/club-gigot/action-points/action-points-advance-sale-result.model";
import { SaveActionPointsDTO } from "../../../../model/club-gigot/action-points/save-action-point.model"
import { ActionPointType } from "../../../../model/club-gigot/action-points/_enum/action-point.enum"

@Component({
    selector: 'cg-dialog-add-product-group',
    templateUrl: './dialog-add-product-group.component.html',
    styleUrls: ['./dialog-add-product-group.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DialogAddProductGroupComponent implements OnInit {

    public loadingData$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public loading$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public advancedSaleProductForm : FormGroup;
    public advancedSaleGroupForm : FormGroup;

    public displayedColumnsProduct = ['product', 'required', 'minimum', 'actions'];
    public tableDataSource: MatTableDataSource<any>;
    public _isEditMode: boolean = false;
    public continue$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public maxExpirationCampaign: number = 40;
    public maxCampaignsToNet: number = 50;

    constructor(private dialogRef: MatDialogRef<DialogAddProductGroupComponent>,
                private actionPointsService : ActionPointsService,
                private cdr: ChangeDetectorRef,
                @Inject (MAT_DIALOG_DATA)
                public data: DialogData) { }

    ngOnInit() {
        this._isEditMode = !!(this.data.actionPointData);
        this.buildForms();
    }

    private setProductForm(): FormGroup[] {
        let form: FormGroup[] = [];
        if(!this._isEditMode){
            for(let product of this.data.selected){
                let formGroup = new FormGroup(
                    {
                        product: new FormControl(product),
                        required: new FormControl(false),
                        minimum: new FormControl(null),
                    }
                )
                form.push(formGroup)
            }
        } else {
            for(let product of this.data.actionPointData.advanceSaleProducts){
                let formGroup = new FormGroup(
                    {
                        product: new FormControl(product.product),
                        required: new FormControl(product.minimum > 0),
                        minimum: new FormControl(product.minimum),
                    })
                if(product.minimum > 0){
                    formGroup.controls['minimum'].setValidators([Validators.required, Validators.min(1)]);
                }
                form.push(formGroup)
            }
        }

        return form;
    }

    public setRequired(formGroup: FormGroup){
        if(formGroup.controls['required'].value){
            formGroup.controls['minimum'].setValidators([Validators.required, Validators.min(1)]);
        }else{
            formGroup.controls['minimum'].clearValidators()
            formGroup.controls['minimum'].setErrors(null)
            formGroup.updateValueAndValidity()
        }
        this.advancedSaleProductForm.updateValueAndValidity()
        this.cdr.detectChanges();
    }

    public buildForms(){
        this.advancedSaleProductForm = new FormGroup({
            products: new FormArray(this.setProductForm())
        })
        this.advancedSaleGroupForm = new FormGroup({
            id : new FormControl((this._isEditMode && this.data.actionPointData != undefined) ? this.data.actionPointData.id : null),
            minimum: new FormControl(this._isEditMode ? this.data.actionPointData.minimumCampaignProducts: null, [Validators.required, Validators.min(1)]),
            maximum: new FormControl(this._isEditMode ? this.data.actionPointData.maximumCampaignProducts: null, [Validators.required]),
            minimumValue: new FormControl(this._isEditMode ? this.data.actionPointData.minimumValue: null, [Validators.required]),
            maximumValue: new FormControl(this._isEditMode ? this.data.actionPointData.maximumValue: null, [Validators.required]),
            points: new FormControl(this._isEditMode ? this.data.actionPointData.points: null, [Validators.required, Validators.min(1)]),
            name : new FormControl(this._isEditMode ? this.data.actionPointData.name : null, [Validators.required]),
            campaignsToNet: new FormControl(this._isEditMode ? this.data.actionPointData.campaignsToNet.toString() : null, [Validators.required]),
            expirationPointsCampaignSequence: new FormControl(this._isEditMode ? (this.data.actionPointData.expirationPointsCampaignSequence - this.data.campaignSequence).toString() : null, [Validators.required]),
            reachId: new FormControl(this._isEditMode ? this.data.actionPointData.reachId : null)
        })
        this.advancedSaleGroupForm.controls['minimum'].setValidators([Validators.required, Validators.min(1), this.validateGroupMinimum()])
        this.tableDataSource = new MatTableDataSource(this.getFormArray().controls);
    }

    public getFormArray(): FormArray{
        return this.advancedSaleProductForm.controls['products'] as FormArray;
    }

    public deleteProduct(index: number): void{
        this.getFormArray().removeAt(index);
        this.tableDataSource =new MatTableDataSource(this.getFormArray().controls);
        this.advancedSaleProductForm.markAsUntouched();
        this.advancedSaleProductForm.markAsTouched();
        this.advancedSaleProductForm.updateValueAndValidity();
    }

    public onNoClick(): void {
        this.dialogRef.close(new ActionPointsAdvanceSaleResult(true));
    }

    public onContinue(): void{
        this.continue$.next(true)
    }

    public onReturn(): void{
        this.continue$.next(false)
    }

    public onCreate():void{
        this.loading$.next(true);
        let advancedSaleProducts: AdvanceSaleGroupProduct[] = []

        for(let data of this.getFormArray().controls){
            let product = new AdvanceSaleGroupProduct(data.value.required? data.value.minimum: 0, data.value.product.id);
            advancedSaleProducts.push(product);
        }

        let actionPointDTO = new ActionPointsDTO();
        actionPointDTO.addActionPoint(ActionPointType.AdvanceSale, {
            id: this.advancedSaleGroupForm.controls['id'].value,
            campaignSequence: this.data.campaignSequence,
            name: this.advancedSaleGroupForm.controls['name'].value,
            campaignsToNet: this.advancedSaleGroupForm.controls['campaignsToNet'].value,
            expirationPointsCampaign: this.advancedSaleGroupForm.controls['expirationPointsCampaignSequence'].value,
            reachId: this.advancedSaleGroupForm.controls['reachId'].value,
            points: this.advancedSaleGroupForm.controls['points'].value,
            minimumCampaignProducts: this.advancedSaleGroupForm.controls['minimum'].value,
            maximumCampaignProducts: this.advancedSaleGroupForm.controls['maximum'].value,
            minimumValue: this.advancedSaleGroupForm.controls['minimumValue'].value,
            maximumValue: this.advancedSaleGroupForm.controls['maximumValue'].value,
            advanceSaleProducts: advancedSaleProducts,
        })

        let dto = new SaveActionPointsDTO(ActionPointType.AdvanceSale, actionPointDTO);

        this.actionPointsService
        .saveActionPointConfiguration(this.data.campaignSequence, dto, this._isEditMode)
        .pipe(
            finalize(() => {
                this.loading$.next(false)
            })
        )
        .subscribe({
            next: (_ : any) => this.dialogRef.close(new ActionPointsAdvanceSaleResult(false, true, this.data.campaignSequence)),
            error: (error : Error) => this.dialogRef.close(new ActionPointsAdvanceSaleResult(false, false, this.data.campaignSequence, error.message))
        })
    }

    /**
     * @description
     *	Se llama cuando se cambia el valor de campaignsToNet. Si expirationPointsCampaign tenia un valor
     *  que es menor al nuevo valor se hace un reset del campo.
     *
     *  @param newValue - el nuevo valor del campo campaignsToNet
     */
     public updateExpiration(newValue: number): void {
        if(this.advancedSaleGroupForm.controls['expirationPointsCampaignSequence'].value <= newValue )
            this.advancedSaleGroupForm.controls['expirationPointsCampaignSequence'].reset()
    }

    /**
     * @description
     *	Convierte un string a un numero (para usar desde el html)
     *
     *  @param value - valor numerico como string
     *  @returns el valor como int
     */
     public getNumber(value: string): number{
        return parseInt(value);
    }

    /**
     * @description
     *	Valida que el minimo de productos sea mayor a la sumatoria del minimo de productos
     *  requeridos
     */
    private validateGroupMinimum(): ValidatorFn {
        return (formControl:FormControl):{[key: string]: any} | null=>{
            let sum = 0;
            this.getFormArray().controls.forEach((control: FormGroup) => {

                let y = control.value;
                if(y.required){
                    sum += y.minimum;
                }
            })
            if( sum > formControl.value){
                return {badMin:true}
            }
            return null
        }
    }
}

export interface DialogData {
    selected: Product[];
    campaignSequence : number;
    actionPointData: AdvanceSaleGroup;
    otherConfigurations: [];
}

